import { Component, inject, OnDestroy } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { ActivatedRoute, ChildActivationEnd, Router, RouterOutlet } from "@angular/router";
import { EMPTY, Observable, Subject, combineLatest } from "rxjs";
import { delay, filter, map, startWith, switchMap, take, takeUntil } from "rxjs/operators";
import {
  AlertKey,
  InfoMessage,
} from "src/app/modules/shared/components/alerts/information-message/information-message.component";
import { FetchPrognosesSnackBarService } from "src/app/services/fetch-prognoses-snack-bar.service";
import { FmsKey } from "src/app/services/translations";
import { PensionPlanProgressService } from "src/app/services/pension-plan-progress.service";
import { PensionPlanRoute, getIsLastRoute } from "src/app/services/pension-plan-progress.utils";
import { LoadingStateService } from "src/app/services/loading-state/loading-state.service";
import { PensionPlanTourService } from "src/app/services/tours/pension-plan-tour.service";
import { getIsNotNullable } from "src/app/utils/utils";
import { ClientDataService } from "../../services/customer-supplied-data/client-data.service";
import {
  NavProgressButton,
  NavProgressButtons,
  NavProgressComponent,
} from "../shared/components/nav-progress/nav-progress.component";
import { LetDirective } from "../shared/directives/let.directive";
import { InformationMessageComponent } from "../shared/components/alerts/information-message/information-message.component";
import { ExpandableDataAlertsComponent } from "../shared/components/expandable-data-alerts/expandable-data-alerts.component";
import { SimulationParamsActivatorComponent } from "./components/simulation-params-activator/simulation-params-activator.component";
import { AsyncPipe } from "@angular/common";
import {
  WithdrawalProfileId,
  WithdrawalProfilesService,
} from "src/app/modules/shared/services/withdrawal-profiles/withdrawal-profiles.service";
import { FmsService } from "src/app/services/fms.service";

export enum NavProgressElements {
  Parameters = "Parameters",
}

@Component({
  selector: "app-pension-plan",
  templateUrl: "./pension-plan.component.html",
  styleUrls: ["./pension-plan.component.scss"],
  standalone: true,
  imports: [
    LetDirective,
    InformationMessageComponent,
    ExpandableDataAlertsComponent,
    RouterOutlet,
    NavProgressComponent,
    SimulationParamsActivatorComponent,
    AsyncPipe,
  ],
})
export class PensionPlanComponent implements OnDestroy {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fetchPrognosesSnackBarService = inject(FetchPrognosesSnackBarService);
  private readonly pensionPlanProgressService = inject(PensionPlanProgressService);
  private readonly pensionPlanTourService = inject(PensionPlanTourService);
  private readonly clientDataService = inject(ClientDataService);
  private readonly withdrawalProfilesService = inject(WithdrawalProfilesService);

  private readonly fmsService = inject(FmsService);
  private readonly loadingStateService: LoadingStateService = inject(LoadingStateService);

  private readonly destroy$: Subject<void>;

  public readonly NavProgressElements = NavProgressElements;
  public readonly AlertKey = AlertKey;

  public readonly isSimulatingDifferentTimes$: Observable<boolean>;
  public readonly simulationParametersPartialWithdrawalByEngagementEnable$;
  public readonly navProgressElements$: Observable<NavProgressElements | null>;
  public readonly progress$: Observable<number | null>;
  public readonly hideNavigation$: Observable<boolean>;
  public readonly navigation$: Observable<NavProgressButtons>;
  public readonly infoMessage = this.fmsService
    .getJSON<InfoMessage[]>("infoMessage")
    .find((m) => m.key === AlertKey.PensionPlan);

  constructor() {
    this.destroy$ = new Subject();
    const isLoaded$ = toObservable(this.loadingStateService.isLoaded);

    this.isSimulatingDifferentTimes$ = this.withdrawalProfilesService.activeWithdrawalProfileId$.pipe(
      map((profileId) => profileId === WithdrawalProfileId.CustomPayoutAge),
      takeUntil(this.destroy$),
    );

    this.navProgressElements$ = this.router.events.pipe(
      filter((routeEvent) => routeEvent instanceof ChildActivationEnd),
      switchMap(() => this.activatedRoute.firstChild?.data ?? EMPTY),
      startWith(this.activatedRoute.snapshot.firstChild?.data),
      map((data) => <NavProgressElements>data?.navProgressElements ?? null),
      takeUntil(this.destroy$),
    );

    this.navigation$ = combineLatest([
      this.pensionPlanProgressService.currentRoute$,
      this.pensionPlanProgressService.planRoutes$,
    ]).pipe(
      filter(([currentRoute]) => !!currentRoute),
      map(([currentRoute, routes]) => this.getNavProgressButtons(currentRoute, routes, getIsLastRoute(currentRoute))),
    );

    this.hideNavigation$ = this.pensionPlanProgressService.currentRoute$.pipe(
      filter(getIsNotNullable),
      map(getIsLastRoute),
      startWith(true),
      delay(0),
    );

    this.progress$ = combineLatest([
      this.pensionPlanProgressService.currentRoute$,
      this.pensionPlanProgressService.planRoutes$,
    ]).pipe(
      // Move to end of queue to prevent stuttering animation
      delay(1),
      // 1 part on first step, 100% on second last
      map(([currentRoute, routes]) =>
        !getIsLastRoute(currentRoute) ? ((this.getRouteIndex(currentRoute, routes) + 1) / routes.length) * 100 : null,
      ),
    );

    this.fetchPrognosesSnackBarService.openSnackBarOnCurrentYearLoading();

    this.pensionPlanProgressService.currentRoute$
      .pipe(
        switchMap((route) =>
          isLoaded$.pipe(
            filter(Boolean),
            take(1),
            map(() => route),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.pensionPlanTourService.startTour());

    this.simulationParametersPartialWithdrawalByEngagementEnable$ =
      this.clientDataService.simulationParametersPartialWithdrawalEnable$.pipe(map(({ enable }) => enable));
  }

  public ngOnDestroy(): void {
    this.fetchPrognosesSnackBarService.destroy();
    this.destroy$.next();
    this.destroy$.complete();
    this.pensionPlanTourService.stopTour();
  }

  private getRouteIndex(target: PensionPlanRoute | undefined, routes: PensionPlanRoute[]): number {
    return routes.findIndex((route) => route.url === target?.url);
  }

  private getNextRoute(
    route: PensionPlanRoute | undefined,
    steps: number,
    routes: PensionPlanRoute[],
  ): PensionPlanRoute | undefined {
    const getIsLooping = (index: number): boolean => index < 0;
    const nextIndex = this.getRouteIndex(route, routes) + steps;

    return getIsLooping(nextIndex) ? undefined : routes?.[nextIndex];
  }

  private getNavProgressButtons(
    currentRoute: PensionPlanRoute | undefined,
    routes: PensionPlanRoute[],
    isLastRoute: boolean,
  ): NavProgressButtons {
    const previousRoute = this.getNextRoute(currentRoute, -1, routes);
    const nextRoute = this.getNextRoute(currentRoute, 1, routes);
    const overrides: NavProgressButtons = window.history.state.navigation;

    return {
      previous: !isLastRoute
        ? getNavProgressButtonFromPensionPlanRoute(
            currentRoute?.previousText ?? "pensionPlan.progress.previous",
            previousRoute,
            overrides?.previous,
          )
        : undefined,
      next: !isLastRoute
        ? getNavProgressButtonFromPensionPlanRoute(
            currentRoute?.nextText ?? "pensionPlan.progress.next",
            nextRoute,
            overrides?.next,
          )
        : undefined,
    };
  }
}

function getNavProgressButtonFromPensionPlanRoute(
  text: FmsKey,
  route?: PensionPlanRoute,
  override?: NavProgressButton,
): NavProgressButton {
  return {
    visible: !!route,
    text,
    link: route?.url,
    ...override,
  };
}
